/**
 * 開発者の OAuth の往復のうち、API とブラウザ（sessionStorage・location）に触る部分（2026-09-27 creator/dev-oauth-ui）。
 * 判断（PKCE・控えの検査）は devOAuth.ts、画面は components/OAuthButtons.tsx・components/Reauth.tsx・pages/OAuthCallback.tsx。
 */
import { api } from './api';
import { readInviteCreditOffer, readSignupRequiresInvite, type InviteCreditOffer, type SignupInviteRequirement } from './inviteCode';
import { NO_LOGIN_OPTIONS, parseLoginOptions, type LoginOptions } from './loginOptions';
import { readOneAccount, type OneAccountInfo } from './oneAccount';
import {
  callbackUrl,
  challengeFor,
  randomToken,
  reauthStillValid,
  savePending,
  type CallbackError,
  type DevOAuthIntent,
  type DevOAuthProvider,
  type ReauthProof,
  type ResumedReauth
} from './devOAuth';

const session = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};
export const pendingStorage = session;

// ---------------------------------------------------------------- 使えるプロバイダ

let infoPromise: Promise<unknown> | null = null;

/**
 * GET /v1/platform/info の応答そのもの（ログイン方法と招待クレジットの額の両方が読む）。
 * 1 回読んだら使い回す（ログイン画面 ↔ 登録画面の行き来で何度も読まない）。失敗したら null（次に開いたときにもう一度読む）
 * 2026-09-30 console/invite-fields: 招待クレジットの額（inviteCredit）を足すため、以前の「LoginOptions だけを使い回す」形から
 *   「応答を 1 回だけ読み、読む側がそれぞれ取り出す」形にした（2 回 fetch しない）
 */
function loadPlatformInfo(): Promise<unknown> {
  infoPromise ??= api<unknown>('/v1/platform/info', { auth: false }).catch(() => {
    infoPromise = null; // 次に開いたときにもう一度読む
    return null;
  });
  return infoPromise;
}

/**
 * GET /v1/platform/info のうちログイン画面に要る分（oauthProviders・passkeyLogin。判断は loginOptions.ts の parseLoginOptions）。
 * 失敗したら空（外部のボタンを出さない = パスワードで入れる）
 * 2026-09-28 creator/login-redesign: 以前は oauthProviders だけを読んでいた。パスキーの印（Core の core/login-passkey）を足すため、
 *   同じ応答を 1 回だけ読んで両方に使う形にした（2 回 fetch しない）
 */
export function loadLoginOptions(): Promise<LoginOptions> {
  return loadPlatformInfo().then((raw) => (raw === null ? NO_LOGIN_OPTIONS : parseLoginOptions(raw)));
}

/**
 * 登録の画面の招待コードの欄に使う inviteCredit（inviteCode.ts の readInviteCreditOffer）。
 * 失敗したら unknown（今までの欄と額なしの説明 = 招待コードを持っている人が入れられなくならない方に倒す）
 */
export function loadInviteCreditOffer(): Promise<InviteCreditOffer> {
  return loadPlatformInfo().then(readInviteCreditOffer);
}

/**
 * 招待制か（platform/info の signupRequiresInvite・inviteCode.ts の readSignupRequiresInvite・2026-10-06 core/invite-only）。
 * 失敗したら unknown（欄は出し、押す前には止めない = 正はサーバー）
 */
export function loadSignupInviteRequirement(): Promise<SignupInviteRequirement> {
  return loadPlatformInfo().then(readSignupRequiresInvite);
}

/**
 * 1 つの KUMODeck アカウントの旗とアカウントの場所（platform/info の oneAccount・accountUrl・oneAccount.ts の readOneAccount・2026-10-07 console/one-account）。
 * 失敗したら off（今のログインのまま = 入れなくなる方に倒さない）
 */
export function loadOneAccountInfo(): Promise<OneAccountInfo> {
  return loadPlatformInfo().then(readOneAccount);
}

/**
 * 使えるプロバイダ（/v1/platform/info の oauthProviders）。ボタンはこれにあるものだけ出す。
 * ChatGPT は窓口が開くまで・Claude は規約で禁止 = サーバーが返さない = ボタンは作らない。
 */
export function loadOAuthProviders(): Promise<DevOAuthProvider[]> {
  return loadLoginOptions().then((o) => o.oauthProviders);
}

// ---------------------------------------------------------------- 開始

export interface StartOptions {
  provider: DevOAuthProvider;
  intent: DevOAuthIntent;
  /** 終わった後に戻る画面（同じサイトの中のパス） */
  returnTo: string;
  /** 再認証の後に元の画面が続ける操作の名前 */
  action?: string;
  /** intent=link の再認証（password か reauthToken） */
  proof?: ReauthProof;
  /** 登録の画面で入れた招待コード（正規化済み・inviteCode.ts の readInviteInput）。intent=login のときだけ控えに入れる */
  inviteCode?: string;
  /** 広告から来た登録（/signup?src=…・2026-10-18・devOAuth.ts の PendingOAuth.fromAd）。intent=login のときだけ控えに入れる */
  fromAd?: boolean;
  /** 登録の画面の URL の src（2026-10-19 ops/usage-origin §3・devOAuth.ts の PendingOAuth.src）。intent=login のときだけ控えに入れる */
  src?: string;
}

/**
 * 往復を始めてプロバイダの画面へ移る。控え（state・verifier）を sessionStorage に置いてから start を呼ぶ。
 * 失敗（ApiError）は投げる（呼んだ画面が文言を出す）。成功したらページが移るので戻らない
 */
export async function startDevOAuth(o: StartOptions): Promise<void> {
  const verifier = randomToken();
  const state = randomToken(16);
  const challenge = await challengeFor(verifier);
  const body = { redirectUrl: callbackUrl(window.location.origin, state), challenge, intent: o.intent, label: 'dashboard', ...(o.proof ?? {}) };
  const r = await api<{ url: string }>(`/v1/developers/oauth/${o.provider}/start`, { method: 'POST', body, auth: o.intent !== 'login' });
  // 控えは start が通ってから置く（失敗した往復の控えを残さない）。置いてから移る
  savePending(session(), {
    state,
    verifier,
    provider: o.provider,
    intent: o.intent,
    returnTo: o.returnTo,
    action: o.action,
    createdAt: Date.now(),
    // 招待コードはログイン（新規登録）の往復だけ（inviteCode.ts の exchangeBody も同じ条件で付ける）
    ...(o.intent === 'login' && o.inviteCode ? { inviteCode: o.inviteCode } : {}),
    ...(o.intent === 'login' && o.fromAd === true ? { fromAd: true } : {}),
    ...(o.intent === 'login' && o.src ? { src: o.src } : {})
  });
  window.location.assign(r.url);
}

// ---------------------------------------------------------------- 再認証の受け渡し（メモリだけ）

/**
 * /oauth/callback で受けた reauthToken を、元の画面へ渡すための置き場。**メモリだけ**（devOAuth.ts の冒頭の WHY）。
 * 失敗（reauth_mismatch・access_denied 等）も同じく元の画面へ渡す（呼んだ画面が「確認できませんでした」を出す）
 */
let resumed: ResumedReauth | null = null;
let resumedFailure: { action: string; code: CallbackError; provider: DevOAuthProvider } | null = null;
const listeners = new Set<() => void>();

export function setResumedReauth(r: ResumedReauth) {
  resumed = r;
  resumedFailure = null;
  listeners.forEach((l) => l());
}
export function setResumedFailure(f: { action: string; code: CallbackError; provider: DevOAuthProvider }) {
  resumed = null;
  resumedFailure = f;
  listeners.forEach((l) => l());
}
/** 期限の残っている証明（切れていれば捨てて null） */
export function peekResumedReauth(): ResumedReauth | null {
  if (resumed && !reauthStillValid(resumed)) resumed = null;
  return resumed;
}
export function peekResumedFailure() {
  return resumedFailure;
}
export function clearResumed() {
  resumed = null;
  resumedFailure = null;
  listeners.forEach((l) => l());
}
export function onResumedChange(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
